import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DataServiceService } from '../services/data-service.service';
import { Customers, Drivers, LoadStatus, LoadTypes, Orders } from '../interfaces';
import { Table } from 'primeng/table';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { forkJoin } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { EditOrderDialogComponent } from '../order-management/edit-order-dialog/edit-order-dialog.component';
import { NewOrderDialogComponent } from '../order-management/new-order-dialog/new-order-dialog.component';
import { TenderDialogComponent } from '../order-management/tender-dialog/tender-dialog.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FilterService } from 'primeng/api';

@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss'
})

export class OrdersComponent implements OnInit {

  @ViewChild(EditOrderDialogComponent) editOrderDialog!: EditOrderDialogComponent;
  @ViewChild(NewOrderDialogComponent) newOrderDialog!: NewOrderDialogComponent;
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('dt1') dt1: any;
  @ViewChild('tenderDialog') tenderDialog!: TenderDialogComponent;

  constructor(private dataServiceService: DataServiceService, private route: ActivatedRoute,
    private cdr: ChangeDetectorRef, private authService: AuthService, private router: Router, private sanitizer: DomSanitizer,
    private filterService: FilterService
  ) { }

  allOrders: Orders[] = []
  drivers: Drivers[] = []
  customers: Customers[] = []
  statusArray: LoadStatus[] = ['Unassigned', 'Scheduled', 'Loaded', 'Delivered', 'Cancelled']
  status = this.statusArray.map(type => ({ label: type, value: type }));

  loadTypesArray: LoadTypes[] = [
    'Cement',
    'Conveyor',
    'Dump',
    'Dump-Salt',
    'Lime',
    'Plastic',
    'Pneumatic-Salt',
    'Pneumatic-Sand',
    'Salt Cake',
    'Soda Ash',
    'Solar Salt',
    'Truck & Pup',
    'Van-Salt'
  ];

  loadType = this.loadTypesArray.map(type => ({ label: type, value: type }));

  searchValue: string | undefined;

  expandedRows: { [key: string]: boolean } = {};

  trailers: any = []

  editVisible: boolean = false
  selectedOrderId: number | undefined
  globalFilterFields: string[] | undefined;
  hasAccess = false;
  // Add a new property to cache receiver options
  currentReceiverOptions: any[] = [];
  currentSupplierOptions: any[] = [];



  // Filtered customer arrays for dropdowns
  get supplierCustomers() {
    return this.customers.filter(customer =>
      customer.customer_type === 'Supplier' || customer.customer_type === 'Both'
    );
  }

  get receiverCustomers() {
    return this.customers.filter(customer =>
      customer.customer_type === 'Receiver' || customer.customer_type === 'Both'
    );
  }

  // Combined customer+address options for suppliers
  get supplierOptions() {
    const options: any[] = [];
    this.supplierCustomers.forEach(customer => {
      if (customer.addresses && customer.addresses.length > 0) {
        // Multiple addresses: create one option per address with compact format
        customer.addresses.forEach((address: any) => {
          options.push({
            name: `${customer.name} (${address.city}, ${address.state})`,
            customer_id: customer.customer_id,
            address_id: address.address_id,
            value: `${customer.customer_id}_${address.address_id}`
          });
        });
      } else {
        // Single/no address: just the customer name
        options.push({
          name: customer.name,
          customer_id: customer.customer_id,
          address_id: null,
          value: customer.customer_id.toString()
        });
      }
    });
    return options;
  }

  // Combined customer+address options for receivers
  get receiverOptions() {
    const options: any[] = [];
    this.receiverCustomers.forEach(customer => {
      if (customer.addresses && customer.addresses.length > 0) {
        // Multiple addresses: create one option per address with compact format
        customer.addresses.forEach((address: any) => {
          options.push({
            name: `${customer.name} (${address.city}, ${address.state})`,
            customer_id: customer.customer_id,
            address_id: address.address_id,
            value: `${customer.customer_id}_${address.address_id}`
          });
        });
      } else {
        // Single/no address: just the customer name
        options.push({
          name: customer.name,
          customer_id: customer.customer_id,
          address_id: null,
          value: customer.customer_id.toString()
        });
      }
    });
    return options;
  }

  ngOnInit() {
    this.filterService.register('excludeDeliveredCancelled', (value: string) => {
      if (!value) return true;
      return value !== 'Delivered' && value !== 'Cancelled';
    });

    this.authService.userProfile$.subscribe((profile) => {
      if (profile?.roles?.includes('Dispatcher') || profile?.roles?.includes('Admin')) {
        this.hasAccess = true;
      } else {
        this.hasAccess = false;
      }
    });

    // Load all required data using forkJoin to ensure proper timing
    forkJoin({
      trailers: this.dataServiceService.getAllTrailers(),
      customers: this.dataServiceService.getAllCustomers(),
      orders: this.dataServiceService.getOrders(),
      drivers: this.dataServiceService.getAllDrivers()
    }).subscribe({
      next: ({ trailers, customers, orders, drivers }) => {
        // Process trailers
        this.trailers = [
          { label: 'No Trailer', value: 0 },
          ...trailers.map((trailer: {
            trailer_id: { toString: () => any; };
            trailer_number: { toString: () => any; };
          }) => ({ label: trailer.trailer_number.toString(), value: trailer.trailer_id.toString() }))
        ];
        this.trailers.sort((a: { label: string; }, b: { label: any; }) => a.label.localeCompare(b.label, undefined, { numeric: true }));

        // Process customers
        this.customers = customers.map((customer: { name: any; customer_id: any; customer_type: any; addresses?: any[]; receivers?: any[]; suppliers?: any[]; }) => ({
          name: customer.name,
          location_id: customer.customer_id,
          customer_id: customer.customer_id,
          customer_type: customer.customer_type,
          addresses: customer.addresses || [],
          receivers: customer.receivers || [],
          suppliers: customer.suppliers || []
        }));

        // Initialize currentReceiverOptions with all receiver options when customers are loaded
        this.currentReceiverOptions = this.receiverOptions;
        this.currentSupplierOptions = this.supplierOptions;

        // Process drivers
        this.drivers = drivers;

        // Now that all dependencies are loaded, subscribe to orders$ for real-time updates
        this.dataServiceService.orders$.subscribe(orders => {
          this.allOrders = orders
            .map(order => ({
              ...order,
              pickup_driver_name: this.getDriverName(order.pickup_driver_id),
              delivery_driver_name: this.getDriverName(order.delivery_driver_id),
              trailer_number: this.getTrailerNumber(order.trailer_id ?? null),
              shipper_name: this.getCustomerName(order.supplier_id),
              receiver_name: this.getCustomerName(order.receiver_id)
            }))
            .sort((a, b) => b.order_id - a.order_id);

          this.cdr.detectChanges();

          // Apply default filter to exclude Delivered/Cancelled when no explicit status filter is set.
          if (this.dt1 && !this.route.snapshot.queryParams['status']) {
            this.dt1.filter(true, 'load_status', 'excludeDeliveredCancelled');
          }
        });
      }
    });

    // Handle route params
    this.route.queryParams.subscribe(params => {
      if (params['orderId'] && params['pageIndex']) {
        setTimeout(() => {
          const pageIndex = parseInt(params['pageIndex']);
          if (this.dt1) {
            this.dt1.first = pageIndex * (this.dt1.rows || 10);

            if (params['openOrder']) {
              this.expandOrderRow(+params['orderId']);
            }
          }
        }, 100);
      }
      if (params['status'] || params['load_type']) {
        setTimeout(() => {
          this.dt1.filter(params['status'], 'load_status', 'equals');
          this.dt1.filter(params['load_type'], 'load_type', 'equals');
        }, 150);
      }
    });


    // Update globalFilterFields to include new fields
    this.globalFilterFields = [
      'trailer_id',
      'supplier_id',
      'receiver_id',
      'pickup_driver_id',
      'delivery_driver_id',
      'load_type',
      'load_status',
      'pickup_number',
      'notes',
      'pickup_driver_name',
      'delivery_driver_name',
      'trailer_number',
      'shipper_name',
      'receiver_name'
    ];
  }


  onCustomerClick(customerId: number, addressId: number) {
    this.router.navigate(['/customers'], {
      queryParams: {
        customerId: customerId,
        addressId: addressId,
        scrollTo: true
      }
    });
  }

  // Helper function for getting driver name
  private getDriverName(driverId: number | null): string {
    if (!driverId) return '';
    const driver = this.drivers.find(d => d.driver_id === driverId);
    return driver ? `${driver.first_name} ${driver.last_name}` : '';
  }

  private getTrailerNumber(trailerId: number | null): string {
    if (!trailerId) return '';
    const trailer = this.trailers.find((t: { value: string }) => t.value === trailerId.toString());
    return trailer ? trailer.label : '';
  }

  ngAfterViewInit() {
    if (this.selectedOrderId) {
      this.cdr.detectChanges();
      this.expandOrderRow(this.selectedOrderId);
    }
  }

  expandOrderRow(orderId: number) {
    // Expand the row for the selected order
    this.expandedRows = { [orderId]: true };
    const orderKey = `order-${orderId}`;

    // Scroll to the selected order row
    setTimeout(() => {
      const element = document.getElementById(orderKey);
      if (element) {
        // Find the scrollable container of the p-table
        const scrollContainer = document.querySelector('.p-datatable-wrapper');
        if (scrollContainer) {
          // Calculate the scroll position
          const elementRect = element.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          const relativeTop = elementRect.top - containerRect.top;
          const centerPosition = relativeTop - (containerRect.height / 2) + (elementRect.height / 2);

          // Scroll the container
          scrollContainer.scrollTo({
            top: scrollContainer.scrollTop + centerPosition,
            behavior: 'smooth'
          });
        }
      }
    }, 100);
  }

  getSeverity(status: string) {
    switch (status) {
      case 'Unassigned':
        return 'danger';
      case 'Scheduled':
        return 'warning';
      case 'Loaded':
        return 'info';
      case 'Delivered':
        return 'success';
      case 'Cancelled':
        return 'contrast';
      default:
        return 'info';  // Default severity if none of the above match
    }
  }

  clear(table: Table) {
    table.clear();
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
  }

  OpenNewOrderDialog() {
    this.newOrderDialog.visible = true;
  }

  toggleRow(orderId: string) {
    this.expandedRows[orderId] = !this.expandedRows[orderId];
  }

  getDriverDetails(driverId: number): Drivers | undefined {
    const driver = this.drivers.find(driver => driver.driver_id === driverId);
    return driver;
  }

  toCentralTime(date: string | Date): string {
    const dt = DateTime.fromISO(date.toString(), { zone: 'utc' });
    const centralTime = dt.setZone('America/Chicago');
    // Check if the time is midnight in Central Time (meaning no specific time was selected)
    if (centralTime.hour === 0 && centralTime.minute === 0 && centralTime.second === 0) {
      return centralTime.toFormat('MM/dd/yyyy');
    }
    return centralTime.toFormat('MM/dd/yyyy h:mm a');
  }

  getCustomerName(locationId: number): string {
    const customer = this.customers.find(c => c.customer_id === locationId);
    return customer ? customer.name || 'Unknown' : 'Unknown';
  }

  // Get customer address for tooltip
  getCustomerAddress(customerId: number, customerAddressId?: number): string {
    const customer = this.customers.find(c => c.customer_id === customerId);
    if (!customer) return 'Address not available';

    if (customer.addresses && customer.addresses.length > 1 && customerAddressId) {
      const specificAddress = customer.addresses.find((addr: any) => addr.address_id === customerAddressId);
      if (specificAddress) {
        return `${specificAddress.address}, ${specificAddress.city}, ${specificAddress.state} ${specificAddress.zipcode}`;
      }
    }

    // Fallback to first address or legacy fields
    if (customer.addresses && customer.addresses.length > 0) {
      const primaryAddress = customer.addresses[0];
      return `${primaryAddress.address}, ${primaryAddress.city}, ${primaryAddress.state} ${primaryAddress.zipcode}`;
    }

    // Legacy fallback for customers without addresses array
    const addressParts = [
      customer.address,
      customer.city,
      customer.state,
      customer.zipcode
    ].filter(part => part && part !== 'Unknown');

    return addressParts.length > 0 ? addressParts.join(', ') : 'Address not available';
  }

  // Get supplier address for tooltip
  getSupplierAddressTooltip(order: any): string {
    return this.getCustomerAddress(order.supplier_id, order.supplier_address_id);
  }

  // Get receiver address for tooltip  
  getReceiverAddressTooltip(order: any): string {
    return this.getCustomerAddress(order.receiver_id, order.receiver_address_id);
  }

  ImportTenders() {
    // Show dialog immediately
    this.tenderDialog.showDialog();

    this.dataServiceService.getTenders().subscribe({
      next: (tenders) => {
        console.log('Received tenders:', tenders);
        this.tenderDialog.loadTenderContent(tenders);
      },
      error: (error) => {
        console.error('Error loading tenders:', error);
        this.tenderDialog.handleError('Error loading tenders. Please try again.');
      }
    });

  }
  selectedHtml: SafeHtml | null = null;
  viewTenderVisible: boolean = false;
  dialogTitle: string = 'View Tender';

  viewTender(html: string, type: string) {
    this.viewTenderVisible = true;
    this.selectedHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    this.dialogTitle = type === 'cancel' ? 'View Cancel Tender' : 'View Tender';
  }
}
